import type { ISODateTimeString, TenantUser } from '@gat-ship/types'
import { globalUtils } from '@gat-ship/utils'
import { Temporal } from '@js-temporal/polyfill'

/**
 * Base data builder for TenantUser objects.
 * This class requires actual values and only provides business logic defaults for boolean flags.
 * Used in application business logic.
 */
export class TenantUserUtils {
  static createTenantUser(
    user: Partial<TenantUser> &
      Pick<TenantUser, 'userId' | 'email' | 'fullName' | 'initials'>
  ): TenantUser {
    const activeFrom = user.activeFrom ?? globalUtils.functions.dateNow(0, 0, 0)
    const activeTo = user.activeTo ?? globalUtils.functions.dateForever()
    const isTenantAdmin = user.isTenantAdmin ?? false
    const canDeactivate = user.canDeactivate ?? true
    const canActivate = user.canActivate ?? true
    const canBeGrantedTenantAdminPrivileges =
      user.canBeGrantedTenantAdminPrivileges ?? true
    const canChangeEmail = user.canChangeEmail ?? true
    const canChangeMobilePhoneNumber = user.canChangeMobilePhoneNumber ?? true
    const canChangeName = user.canChangeName ?? true
    const canChangeProfileImage = user.canChangeProfileImage ?? true
    const canHaveTenantAdminPrivilegesRevoked =
      user.canHaveTenantAdminPrivilegesRevoked ?? true
    const canScheduleDeactivation = user.canScheduleDeactivation ?? true
    const isActive = user.isActive ?? true

    const newUser = this.setBusinessLogicProperties({
      ...user,
      canScheduleActivation: user.canScheduleActivation ?? true,
      activeFrom,
      activeTo,
      isTenantAdmin,
      isActive,
      canDeactivate,
      canActivate,
      canBeGrantedTenantAdminPrivileges,
      canChangeEmail,
      canChangeMobilePhoneNumber,
      canChangeName,
      canChangeProfileImage,
      canHaveTenantAdminPrivilegesRevoked,
      canScheduleDeactivation
    })
    return newUser
  }

  static isActiveTenantUser({
    activeFrom,
    activeTo
  }: {
    activeFrom: ISODateTimeString
    activeTo: ISODateTimeString
  }) {
    const now = Temporal.Now.zonedDateTimeISO(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ).toInstant()
    const _activeFrom = Temporal.Instant.from(activeFrom)
    const _activeTo = Temporal.Instant.from(activeTo)
    const isActive =
      Temporal.Instant.compare(_activeFrom, now) <= 0 &&
      Temporal.Instant.compare(_activeTo, now) >= 0
    return isActive
  }

  static isScheduledForActivation(user: TenantUser) {
    const activeFromInstant = Temporal.Instant.from(user.activeFrom)
    const nowInstant = Temporal.Now.instant()

    const compare = Temporal.Instant.compare(nowInstant, activeFromInstant)

    const isScheduledForActivation =
      !this.isActiveTenantUser(user) && compare <= 0

    // const numberOfDays = duration.days
    return isScheduledForActivation
  }

  static isScheduledForDeactivation(user: TenantUser) {
    if (!this.isActiveTenantUser(user)) {
      return false
    }

    const threshold = Temporal.Instant.from('2099-01-01T00:00:00Z').subtract({
      hours: 24
    })
    const dateInstant = Temporal.Instant.from(user.activeTo)
    const compare = Temporal.Instant.compare(dateInstant, threshold)
    const isScheduledForDeactivation = compare <= 0
    // const numberOfDays = duration.days
    return isScheduledForDeactivation
  }

  private static setBusinessLogicProperties(user: TenantUser): TenantUser {
    const isActive = this.isActiveTenantUser({
      activeTo: user.activeTo,
      activeFrom: user.activeFrom
    })
    return {
      ...user,
      canActivate: !isActive,
      isActive,
      canDeactivate: isActive,
      canScheduleDeactivation: isActive,
      canScheduleActivation: !isActive,
      canHaveTenantAdminPrivilegesRevoked: user.isTenantAdmin,
      canBeGrantedTenantAdminPrivileges: !user.isTenantAdmin,
      canChangeEmail: true,
      canChangeMobilePhoneNumber: true,
      canChangeName: true,
      canChangeProfileImage: true
    }
  }

  static changeNameOfTenantUser(
    orgTenantUser: TenantUser,
    fullName: string,
    initials: string
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      fullName,
      initials
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static changeEmailOfTenantUser(
    orgTenantUser: TenantUser,
    email: string
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      email
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static changeProfileImageOfTenantUser(
    orgTenantUser: TenantUser,
    imageUrl?: string
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      imageUrl
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static changePhoneNumberOfTenantUser(
    orgTenantUser: TenantUser,
    mobilePhoneNumber?: string
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      mobilePhone: mobilePhoneNumber
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static grantTenantAdminPrivilegesToTenantUser(
    orgTenantUser: TenantUser
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      isTenantAdmin: true
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static revokeTenantAdminPrivilegesFromTenantUser(
    orgTenantUser: TenantUser
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      isTenantAdmin: false
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static activateTenantUser(
    orgTenantUser: TenantUser,
    activeFrom: ISODateTimeString | undefined
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      activeTo: globalUtils.functions.dateForever(),
      activeFrom: activeFrom ?? globalUtils.functions.dateNow(0, 0, 0)
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static scheduleDeactivationOfTenantUser(
    orgTenantUser: TenantUser,
    deactivateAt: ISODateTimeString
  ): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      activeTo: deactivateAt
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  /**
   * Deactivates a tenant user by setting the activeTo date to the current date.
   * @param orgTenantUser - The tenant user to deactivate.
   * @returns The deactivated tenant user.
   */
  static deactivateTenantUser(orgTenantUser: TenantUser): TenantUser {
    const newTenantUser = {
      ...orgTenantUser,
      activeTo: globalUtils.functions.dateNow(0, 0, 0),
      activeFrom: globalUtils.functions.dateNow(0, 0, 0)
    }
    return this.setBusinessLogicProperties(newTenantUser)
  }

  static removeActivationSchedule(orgTenantUser: TenantUser) {
    return this.deactivateTenantUser(orgTenantUser)
  }

  static removeDeactivationSchedule(orgTenantUser: TenantUser) {
    return this.scheduleDeactivationOfTenantUser(
      orgTenantUser,
      globalUtils.functions.dateForever()
    )
  }
}
